/**
 * Meta (Facebook) Pixel.
 *
 * SETUP: paste your pixel ID below. Find it in Meta Events Manager →
 * Data Sources → your pixel (a 15–16 digit number). Until it is filled in,
 * every function here is a no-op, so nothing breaks and no bad data is sent.
 */
const META_PIXEL_ID = '1403047198378214';

type FbqCommand = 'init' | 'track' | 'trackCustom' | 'consent';
type FbqEventParameters = {
  value?: number;
  currency?: string;
  content_name?: string;
  content_type?: string;
  content_ids?: string[];
  num_items?: number;
};

declare global {
  interface Window {
    fbq?: ((command: FbqCommand, ...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue?: unknown[];
      push?: unknown;
      loaded?: boolean;
      version?: string;
    };
    _fbq?: Window['fbq'];
  }
}

/**
 * Installs the pixel and fires the initial PageView. This — not Purchase — is
 * the code that belongs on every page. Call once at app boot.
 */
export function initializeMetaPixel(): void {
  if (typeof window === 'undefined' || !META_PIXEL_ID) return;
  if (document.getElementById('meta-pixel-src')) return;

  // Faithful port of Meta's official bootstrap snippet. It queues commands in
  // a real `arguments` object (not a spread array) and inserts the script
  // before the first existing <script>, exactly as Meta's loader expects —
  // fbevents.js replays that queue on load, and it is picky about the shape.
  if (!window.fbq) {
    const n = function () {
      // eslint-disable-next-line prefer-rest-params
      const a = arguments;
      if (n.callMethod) {
        n.callMethod.apply(n, a as unknown as unknown[]);
      } else {
        n.queue?.push(a);
      }
    } as unknown as NonNullable<Window['fbq']>;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    window.fbq = n;
    if (!window._fbq) window._fbq = n;
  }

  const script = document.createElement('script');
  script.id = 'meta-pixel-src';
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');

  // A <noscript> tracking pixel can't be added (this app has no editable HTML
  // shell), so JS-disabled visits are simply not counted. That is a negligible
  // share of traffic and does not affect Purchase attribution.
}

/**
 * Fires PageView for client-side route changes. The site is a single-page app,
 * so navigation never reloads the document and the pixel would otherwise
 * record only the first page of every visit.
 */
export function trackMetaPageView(): void {
  if (typeof window === 'undefined' || !window.fbq || !META_PIXEL_ID) return;
  window.fbq('track', 'PageView');
}

/**
 * Purchase — fired ONCE at order confirmation, never on page load.
 * eventID matches the Google Ads transaction ID so Meta can deduplicate if a
 * server-side Conversions API event is ever added for the same order.
 */
export function trackMetaPurchase({
  transactionId,
  value,
  materialNames
}: {
  transactionId: string;
  value: number;
  materialNames: string[];
}): void {
  if (typeof window === 'undefined' || !window.fbq || !META_PIXEL_ID) return;
  if (!transactionId) return;

  const parameters: FbqEventParameters = {
    value: Number(value.toFixed(2)),
    currency: 'USD',
    content_type: 'product',
    content_ids: materialNames,
    num_items: materialNames.length
  };

  window.fbq('track', 'Purchase', parameters, {
    eventID: transactionId
  });
}

/**
 * InitiateCheckout — the shopper has a truck loaded and opened checkout.
 * Useful as a mid-funnel audience for retargeting abandoned carts.
 */
export function trackMetaInitiateCheckout({
  value,
  materialNames
}: {
  value: number;
  materialNames: string[];
}): void {
  if (typeof window === 'undefined' || !window.fbq || !META_PIXEL_ID) return;

  const parameters: FbqEventParameters = {
    value: Number(value.toFixed(2)),
    currency: 'USD',
    content_type: 'product',
    content_ids: materialNames,
    num_items: materialNames.length
  };

  window.fbq('track', 'InitiateCheckout', parameters);
}
